import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Smartphone, X, ArrowRight } from "lucide-react";
import usePwaInstall from "@/lib/usePwaInstall";

const DISMISS_KEY = "jt_homepage_app_card_dismissed_at";
const DISMISS_DAYS = 30;     // pergunta de novo só após 1 mês

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

function ga4(name, params = {}) {
  try {
    if (typeof window?.gtag === "function") {
      window.gtag("event", name, params);
    }
  } catch { /* noop */ }
}

/**
 * HomeAppPromoCard — mini-card "Estude pelo App" exibido na home
 * logo abaixo do Hero, apenas em mobile (md:hidden).
 *
 * NÃO renderiza se:
 *   • já está rodando em modo standalone (usuário já instalou)
 *   • o card foi dispensado nos últimos 30 dias
 *   • viewport é desktop (md:hidden já cuida disso via CSS)
 *
 * Estrategicamente diferente do PwaInstallBanner:
 *   - sempre visível (não depende de scroll/timer)
 *   - aponta para /app (não dispara prompt nativo) — mais "soft sell"
 *   - usa a imagem editorial /brand/app-1200x630.jpg como visual
 *   - persiste dismissal por 30 dias (não 7 como o banner) porque é
 *     fixo no topo, mais conspícuo, então a "pergunta" deve ser mais
 *     espaçada para evitar fadiga.
 */
export default function HomeAppPromoCard() {
  const { isStandalone } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setDismissed(wasDismissedRecently());
  }, []);

  if (!mounted) return null;
  if (isStandalone) return null;
  if (dismissed) return null;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    setDismissed(true);
    ga4("home_app_card_dismissed");
  };

  const handleClick = () => {
    ga4("home_app_card_clicked");
  };

  return (
    <div className="md:hidden px-4 -mt-4 mb-2" data-testid="home-app-promo-card">
      <Link
        to="/app"
        onClick={handleClick}
        className="block rounded-xl border border-amber-500/30 bg-slate-900/80 backdrop-blur overflow-hidden shadow-lg shadow-amber-900/10 active:scale-[0.98] transition group"
        data-testid="home-app-promo-link"
      >
        <div className="flex items-stretch gap-3 p-2 pr-3">
          {/* Thumbnail — usa a imagem editorial como prova visual */}
          <div className="shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-slate-950 border border-slate-800">
            <img
              src="/brand/app-1200x630.jpg"
              alt="JurisTube App"
              loading="lazy"
              className="w-full h-full object-cover object-right group-hover:scale-[1.05] transition-transform"
              width="200"
              height="200"
            />
          </div>

          <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
            <div className="flex items-center gap-1.5">
              <Smartphone className="w-3 h-3 text-amber-300" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300/90">
                Novo
              </span>
            </div>
            <div className="text-[14px] font-bold text-slate-100 leading-tight">
              Estude pelo App
            </div>
            <div className="text-[11px] text-slate-400 leading-snug">
              Instale o JurisTube na tela inicial em 2 toques.
            </div>
            <div className="text-[11px] text-amber-300 font-semibold inline-flex items-center gap-0.5 mt-0.5">
              Ver como
              <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
            </div>
          </div>

          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dispensar"
            className="shrink-0 self-start text-slate-600 hover:text-slate-300 transition p-1 -mr-1"
            data-testid="home-app-promo-dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </Link>
    </div>
  );
}
